import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  getChargers,
  getChargerConnectors,
  getModeratorManualChargeWallet,
  getModeratorManualChargeActive,
  startModeratorManualCharge,
  stopModeratorManualCharge,
} from '../lib/api';
import { canUseModeratorManualCharge, MANUAL_CHARGE_FIXED_CLIENT_EMAIL } from '../lib/manualChargeAccess';
import { extractApiErrorMessage, formatCurrency } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Play, Square, Plug, Wallet, Zap, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const ManualCharge = () => {
  const { user } = useAuth();
  const allowed = canUseModeratorManualCharge(user);

  const [wallet, setWallet] = useState(null);
  const [chargers, setChargers] = useState([]);
  const [connectors, setConnectors] = useState([]);
  const [chargerId, setChargerId] = useState('');
  const [connectorId, setConnectorId] = useState('1');
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [loadingWallet, setLoadingWallet] = useState(true);
  const [loadingChargers, setLoadingChargers] = useState(true);
  const [starting, setStarting] = useState(false);
  const [stoppingId, setStoppingId] = useState(null);
  const [activeSessions, setActiveSessions] = useState([]);
  // Array of pending starts (one per initiated charge, before cable connect)
  const [pendingStarts, setPendingStarts] = useState([]);

  const fetchWallet = useCallback(async () => {
    try {
      setLoadingWallet(true);
      const res = await getModeratorManualChargeWallet();
      setWallet(res.data);
    } catch (error) {
      toast.error(extractApiErrorMessage(error, 'Gabim në ngarkimin e krediteve'));
    } finally {
      setLoadingWallet(false);
    }
  }, []);

  const fetchActive = useCallback(async () => {
    try {
      const res = await getModeratorManualChargeActive();
      const sessions = res.data?.sessions || [];
      setActiveSessions(sessions);
      // Remove pending entries whose charger+connector now has a real session
      if (sessions.length > 0) {
        setPendingStarts((prev) =>
          prev.filter(
            (p) =>
              !sessions.some(
                (s) =>
                  s.charger_ocpp_id === p.charger_ocpp_id &&
                  String(s.connector_id) === String(p.connector_id)
              )
          )
        );
      }
    } catch (error) {
      console.error('Active sessions error:', error);
    }
  }, []);

  const fetchChargersList = useCallback(async () => {
    try {
      setLoadingChargers(true);
      const res = await getChargers();
      const items = Array.isArray(res.data)
        ? res.data
        : res.data?.chargers || res.data?.items || [];
      const sorted = [...items].sort((a, b) => {
        if (Boolean(a.is_online) === Boolean(b.is_online)) {
          return String(a.name || a.charger_id || '').localeCompare(
            String(b.name || b.charger_id || ''),
            'sq'
          );
        }
        return a.is_online ? -1 : 1;
      });
      setChargers(sorted);
      if (sorted.length === 1) setChargerId(sorted[0].id);
    } catch (error) {
      toast.error(extractApiErrorMessage(error, 'Gabim në ngarkimin e karikuesve'));
    } finally {
      setLoadingChargers(false);
    }
  }, []);

  useEffect(() => {
    if (!allowed) return undefined;
    fetchWallet();
    fetchChargersList();
    fetchActive();
    return undefined;
  }, [allowed, fetchWallet, fetchChargersList, fetchActive]);

  useEffect(() => {
    if (!chargerId) {
      setConnectors([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await getChargerConnectors(chargerId);
        const list = Array.isArray(res.data) ? res.data : res.data?.connectors || [];
        if (cancelled) return;
        setConnectors(list);
        if (list.length > 0) {
          const free = list.find(
            (c) =>
              !['Charging', 'Preparing', 'SuspendedEV', 'SuspendedEVSE', 'Finishing'].includes(
                c.status
              )
          );
          setConnectorId(String((free || list[0]).connector_id));
        } else {
          setConnectorId('1');
        }
      } catch {
        if (!cancelled) {
          setConnectors([]);
          setConnectorId('1');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [chargerId]);

  // Poll active sessions as long as there's anything to track
  useEffect(() => {
    if (!allowed) return undefined;
    if (activeSessions.length === 0 && pendingStarts.length === 0) return undefined;
    const timer = setInterval(fetchActive, 3000);
    return () => clearInterval(timer);
  }, [allowed, activeSessions.length, pendingStarts.length, fetchActive]);

  // Auto-expire pending banners after 2 min
  useEffect(() => {
    if (pendingStarts.length === 0) return undefined;
    const timer = setTimeout(() => {
      setPendingStarts((prev) => {
        const cutoff = Date.now() - 120000;
        return prev.filter((p) => p.addedAt > cutoff);
      });
    }, 120000);
    return () => clearTimeout(timer);
  }, [pendingStarts.length]);

  if (!allowed) {
    return <Navigate to="/dashboard" replace />;
  }

  // Build set of occupied charger+connector keys from active sessions
  const occupiedKeys = new Set(
    activeSessions.map((s) => `${s.charger_id}_${s.connector_id}`)
  );
  // Also include pending starts (already sent RemoteStart but no session yet)
  pendingStarts.forEach((p) => {
    if (p.charger_id && p.connector_id) {
      occupiedKeys.add(`${p.charger_id}_${p.connector_id}`);
    }
  });

  const selectedOccupied = chargerId
    ? occupiedKeys.has(`${chargerId}_${parseInt(connectorId, 10) || 1}`)
    : false;

  const handleStart = async () => {
    const raw = (amount || '').toString().trim();
    const amountProvided = raw !== '';
    const parsed = amountProvided ? parseFloat(raw) : null;

    if (amountProvided && (Number.isNaN(parsed) || parsed < 50)) {
      toast.error('Nëse vendos limit, minimumi është 50 LEK');
      return;
    }
    if (!chargerId) {
      toast.error('Zgjidh karikuesin');
      return;
    }
    if (selectedOccupied) {
      toast.error('Kjo portë ka tashmë karikim aktiv. Zgjidh portë tjetër.');
      return;
    }

    try {
      setStarting(true);
      const connIdInt = parseInt(connectorId, 10) || 1;
      const payload = {
        charger_id: chargerId,
        connector_id: connIdInt,
        notes: notes || 'Karikim manual walk-in nga moderatori',
      };
      if (amountProvided) payload.amount_lek = parsed;

      const res = await startModeratorManualCharge(payload);
      const maxCost = res.data?.max_cost_lek ?? (amountProvided ? parsed : 5000);
      toast.success(res.data?.message || `Karikimi u nis (limit ${maxCost} LEK)`);

      // Add to pending list (one per started charge)
      setPendingStarts((prev) => [
        ...prev,
        {
          key: `${res.data?.charger_ocpp_id || chargerId}_${connIdInt}`,
          charger_id: chargerId,
          charger_ocpp_id: res.data?.charger_ocpp_id,
          connector_id: connIdInt,
          max_cost_lek: maxCost,
          addedAt: Date.now(),
        },
      ]);

      setAmount('');
      setNotes('');
      await Promise.all([fetchActive(), fetchWallet()]);
    } catch (error) {
      toast.error(extractApiErrorMessage(error, 'Gabim në nisjen e karikimit'));
    } finally {
      setStarting(false);
    }
  };

  const handleStop = async (sessionId) => {
    if (!window.confirm('Ndalo karikimin manual?')) return;
    try {
      setStoppingId(sessionId);
      const res = await stopModeratorManualCharge(sessionId);
      toast.success(res.data?.message || 'Karikimi u ndalua');
      await Promise.all([fetchActive(), fetchWallet()]);
    } catch (error) {
      toast.error(extractApiErrorMessage(error, 'Gabim në ndalimin e karikimit'));
    } finally {
      setStoppingId(null);
    }
  };

  const available = Number(wallet?.available_balance ?? wallet?.balance ?? 0);
  const defaultCap = Number(wallet?.default_max_cost_lek ?? 5000);

  // Pending banners: only show entries that don't yet have a matching active session
  const visiblePending = pendingStarts.filter(
    (p) =>
      !activeSessions.some(
        (s) =>
          s.charger_ocpp_id === p.charger_ocpp_id &&
          String(s.connector_id) === String(p.connector_id)
      )
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Karikim Manual</h1>
          <p className="text-zinc-400 text-sm mt-1">
            Nis karikim walk-in. Faturimi shkon te klienti i fiksuar
            ({MANUAL_CHARGE_FIXED_CLIENT_EMAIL}): përdoren kreditet totale
            (global + të gjitha portofolët e stacioneve), jo llogaria e moderatorit.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => { fetchWallet(); fetchActive(); }}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Rifresko
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="bg-zinc-900/50 border-white/10">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-11 h-11 rounded-lg bg-emerald-500/15 flex items-center justify-center">
              <Wallet className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <p className="text-xs text-zinc-400">Kredite klienti (të disponueshme)</p>
              <p className="text-2xl font-bold text-white">
                {loadingWallet ? '…' : `${available.toLocaleString()} LEK`}
              </p>
              {!loadingWallet && wallet?.full_name && (
                <p className="text-xs text-zinc-500 mt-1">
                  {wallet.full_name} · {wallet.email || MANUAL_CHARGE_FIXED_CLIENT_EMAIL}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-white/10 lg:col-span-2">
          <CardContent className="p-4 text-sm text-zinc-300 space-y-1">
            <p>
              Zgjidh karikuesin dhe portën, nis karikimin,
              ndiq koston live dhe ndalo me Stop. Mund të nisësh shumë karikime paralele.
            </p>
            <p className="text-zinc-500">
              Limit opsional: nëse e lë bosh, ndalon automatikisht në {defaultCap.toLocaleString()} LEK
              (ose kur mbarojnë kreditet e klientit).
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Pending banners — one per initiated charge waiting for cable */}
      {visiblePending.map((p) => (
        <Card key={p.key} className="border-amber-500/30 bg-amber-500/5">
          <CardContent className="p-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Plug className="w-5 h-5 text-amber-400 animate-pulse" />
              <div>
                <p className="font-semibold text-white">Duke pritur lidhjen e kabllit…</p>
                <p className="text-sm text-zinc-400">
                  RemoteStart u dërgua
                  {p.max_cost_lek != null
                    ? ` · limit ${Number(p.max_cost_lek).toLocaleString()} LEK`
                    : ''}
                  {p.charger_ocpp_id ? ` · ${p.charger_ocpp_id}` : ''}
                  {p.connector_id ? ` · Porta ${p.connector_id}` : ''}
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setPendingStarts((prev) => prev.filter((x) => x.key !== p.key))
              }
            >
              Mbyll
            </Button>
          </CardContent>
        </Card>
      ))}

      {/* Active sessions */}
      {activeSessions.map((session) => {
        const isPreparing = ['Preparing', 'preparing', 'pregatitje'].includes(session.status);
        const maxCost = session.max_cost_lek != null ? Number(session.max_cost_lek) : null;
        const cost = Number(session.cost_lek || 0);
        const progress =
          maxCost && maxCost > 0 ? Math.min(100, Math.round((cost / maxCost) * 100)) : null;
        const remaining =
          session.remaining_budget_lek != null
            ? Number(session.remaining_budget_lek)
            : maxCost != null
              ? Math.max(0, maxCost - cost)
              : null;
        const isStopping = stoppingId === session.id;

        return (
          <Card key={session.id} className={isPreparing ? "border-amber-500/30 bg-amber-500/5" : "border-emerald-500/30 bg-emerald-500/5"}>
            <CardContent className="p-4">
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div className="flex items-start gap-3">
                  <div className={`w-11 h-11 rounded-lg flex items-center justify-center shrink-0 ${isPreparing ? 'bg-amber-500/15' : 'bg-emerald-500/15'}`}>
                    {isPreparing
                      ? <Plug className="w-5 h-5 text-amber-400 animate-pulse" />
                      : <Zap className="w-5 h-5 text-emerald-400 animate-pulse" />}
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-white">
                        {isPreparing ? 'Duke pritur lidhjen e kabllit…' : 'Karikim aktiv'}
                      </p>
                      <Badge className={isPreparing ? "bg-amber-500/20 text-amber-400 border-0" : "bg-emerald-500/20 text-emerald-400 border-0"}>
                        {session.status || 'Charging'}
                      </Badge>
                      <Badge variant="outline" className="border-amber-500/40 text-amber-400">
                        Klienti i fiksuar
                      </Badge>
                    </div>
                    <p className="text-sm text-zinc-400 mt-1">
                      {session.charger_name || session.charger_ocpp_id || session.charger_id}
                      {session.connector_id ? ` · Porta ${session.connector_id}` : ''}
                      {session.site_name ? ` · ${session.site_name}` : ''}
                    </p>
                    {isPreparing ? (
                      <p className="text-sm text-zinc-400 mt-2">
                        RemoteStart u dërgua. Lidh kablloin te charger-i për të filluar karikimin.
                        {maxCost != null ? ` Limit: ${maxCost.toFixed(0)} LEK.` : ''}
                      </p>
                    ) : (
                      <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                        <div>
                          <p className="text-zinc-500 text-xs">Kosto</p>
                          <p className="font-semibold text-white">{cost.toFixed(0)} LEK</p>
                        </div>
                        <div>
                          <p className="text-zinc-500 text-xs">Energji</p>
                          <p className="font-semibold text-white">
                            {Number(session.energy_kwh || 0).toFixed(2)} kWh
                          </p>
                        </div>
                        <div>
                          <p className="text-zinc-500 text-xs">Fuqi</p>
                          <p className="font-semibold text-white">
                            {Number(session.current_power_kw || 0).toFixed(1)} kW
                          </p>
                        </div>
                        <div>
                          <p className="text-zinc-500 text-xs">
                            {maxCost != null ? 'Buxheti' : 'Kohëzgjatja'}
                          </p>
                          <p className="font-semibold text-white">
                            {maxCost != null
                              ? `${cost.toFixed(0)} / ${maxCost.toFixed(0)} LEK`
                              : `${Number(session.duration_minutes || 0).toFixed(0)} min`}
                          </p>
                        </div>
                      </div>
                    )}
                    {progress != null && (
                      <div className="mt-3 max-w-md">
                        <div className="flex justify-between text-xs text-zinc-400 mb-1">
                          <span>Progresi i buxhetit</span>
                          <span>
                            {remaining != null
                              ? `${remaining.toFixed(0)} LEK mbeten`
                              : `${progress}%`}
                          </span>
                        </div>
                        <div className="h-2 rounded-full bg-zinc-800 overflow-hidden">
                          <div
                            className={`h-full transition-all duration-500 ${
                              progress >= 90 ? 'bg-amber-500' : 'bg-emerald-500'
                            }`}
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <Button
                  variant={isPreparing ? "outline" : "destructive"}
                  className={`shrink-0 ${isPreparing ? 'border-amber-500/50 text-amber-400 hover:bg-amber-500/10' : ''}`}
                  disabled={isStopping}
                  onClick={() => handleStop(session.id)}
                >
                  <Square className="w-4 h-4 mr-2" />
                  {isStopping ? 'Duke anuluar...' : isPreparing ? 'Anulo' : 'Ndalo Karikimin'}
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      })}

      {/* Start new charge form — always visible */}
      <Card className="bg-zinc-900/50 border-white/10">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Play className="w-5 h-5 text-emerald-500" />
            Nis karikim të ri
          </CardTitle>
          <CardDescription>
            Faturimi shkon te klienti i fiksuar {wallet?.full_name || MANUAL_CHARGE_FIXED_CLIENT_EMAIL}
            {' '}({formatCurrency(available, 'LEK')} të disponueshme).
            {activeSessions.length > 0 && (
              <span className="ml-1 text-emerald-400">
                · {activeSessions.length} karikim{activeSessions.length > 1 ? 'e' : ''} aktiv{activeSessions.length > 1 ? 'e' : ''}
              </span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Karikuesi *</Label>
              <Select
                value={chargerId}
                onValueChange={setChargerId}
                disabled={loadingChargers}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700">
                  <SelectValue
                    placeholder={loadingChargers ? 'Duke ngarkuar...' : 'Zgjidh karikuesin'}
                  />
                </SelectTrigger>
                <SelectContent>
                  {chargers.map((ch) => (
                    <SelectItem key={ch.id} value={ch.id} disabled={!ch.is_online}>
                      {ch.name || ch.charger_id || ch.id}
                      {ch.is_online ? ' · Online' : ' · Offline'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-300">Porta</Label>
              <Select
                value={connectorId}
                onValueChange={setConnectorId}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700">
                  <SelectValue placeholder="Zgjidh portën" />
                </SelectTrigger>
                <SelectContent>
                  {(connectors.length > 0
                    ? connectors
                    : [{ connector_id: 1, status: 'Available' }]
                  ).map((c) => {
                    const connKey = `${chargerId}_${c.connector_id}`;
                    const isOccupied = occupiedKeys.has(connKey);
                    return (
                      <SelectItem
                        key={c.connector_id}
                        value={String(c.connector_id)}
                        disabled={isOccupied}
                      >
                        Porta {c.connector_id}
                        {c.connector_type ? ` (${c.connector_type})` : ''}
                        {isOccupied ? ' · E zënë' : c.status ? ` · ${c.status}` : ''}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-300">Limit (LEK) — opsionale</Label>
              <Input
                type="number"
                min="50"
                step="50"
                placeholder={`Bosh = ${defaultCap.toLocaleString()} LEK`}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-300">Shënime (opsionale)</Label>
              <Input
                type="text"
                placeholder="p.sh. Klient walk-in"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              onClick={handleStart}
              disabled={
                starting ||
                !chargerId ||
                selectedOccupied ||
                available < 50 ||
                (amount.trim() !== '' &&
                  (Number.isNaN(parseFloat(amount)) || parseFloat(amount) < 50))
              }
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <Play className="w-4 h-4 mr-2" />
              {starting ? 'Duke nisur...' : 'Nis Karikimin'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ManualCharge;
